import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiCalendar, FiExternalLink, FiFileText, FiBuilding } = FiIcons;

const ItemDetail = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchItem();
  }, [id]);

  const fetchItem = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/boe/items/${id}`);
      setItem(response.data.item);
      setError('');
    } catch (error) {
      console.error('Error fetching item:', error);
       if (error.response?.status === 404) {
        setError('not_found');
      } else {
        setError('fetch_error');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "d 'de' MMMM 'de' yyyy", { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  const getRangoColor = (rango) => {
    const colors = {
      'Ley Orgánica': 'bg-purple-100 text-purple-800 border-purple-200',
      'Ley': 'bg-blue-100 text-blue-800 border-blue-200',
      'Real Decreto-ley': 'bg-red-100 text-red-800 border-red-200',
      'Real Decreto': 'bg-orange-100 text-orange-800 border-orange-200',
      'Orden': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[rango] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error === 'not_found' || error === 'fetch_error') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6" >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" /> Volver
          </Link>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4"> Contenido no disponible </h1>
            <p className="text-gray-600 mb-6"> El elemento solicitado no existe o ha ocurrido un error. </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-blue-800 hover:text-blue-900 mb-6 transition-colors" >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" /> Volver al boletín
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200"
        >
          {/* Header */}
          <div className="border-b border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-4">
                  <SafeIcon icon={FiFileText} className="w-6 h-6 text-blue-800 flex-shrink-0" />
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                    <span>{formatDate(item.fecha_publicacion)}</span>
                  </div>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {item.titulo}
                </h1>
              </div>
              {item.rango_normativo && (
                <div className={`px-4 py-2 rounded-lg border text-sm font-medium ${getRangoColor(item.rango_normativo)}`}>
                  {item.rango_normativo}
                </div>
              )}
            </div>
            {item.organismo && (
              <div className="flex items-center space-x-2 text-gray-600">
                <SafeIcon icon={FiBuilding} className="w-4 h-4" />
                <span className="font-medium">Organismo:</span> <span>{item.organismo}</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {item.summary_short && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3"> Resumen </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-gray-800 leading-relaxed"> {item.summary_short} </p>
                </div>
              </div>
            )}
            
            {item.summary_practico && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3"> Análisis práctico </h2>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-gray-800 leading-relaxed whitespace-pre-line"> {item.summary_practico} </p>
                </div>
              </div>
            )}

            {/* Disclaimer */}
            <div className="text-xs text-gray-400 italic mt-8 border-t pt-4">
               Este contenido es un resumen no oficial. La única fuente válida es el <a href={item.url_oficial} target="_blank" className="underline">documento original del BOE</a>.
            </div>

            {/* Actions */}
            <div className="pt-2">
              <a
                href={item.url_oficial}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-800 hover:bg-blue-900 transition-colors"
              >
                <SafeIcon icon={FiExternalLink} className="w-5 h-5 mr-2" />
                Leer texto oficial en BOE
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ItemDetail;